import { logError } from './misc';

export class HttpError extends Error {
    public status: number;

    public constructor(status: number, message: string, options?: ErrorOptions) {
        super(message, options);
        this.name = 'HttpError';
        this.status = status;
    }
}

interface APIRequestInfo {
    method?: string;
    data?: unknown;
    body?: BodyInit;
    headers?: Record<string, string>;
    disableCSRF?: boolean;
}

export interface ListResponse {
    offset: number;
    count: number;
    total: number;
    items: unknown[];
}

interface CSRFRequestResponse {
    csrf_token: string;
}

const READ_ONLY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export class APIAccessor {
    private csrfToken: string | null = null;

    // eslint-disable-next-line unicorn/consistent-class-member-order -- conflicts with @typescript-eslint/member-ordering, which requires instance fields before static methods
    public static isReadOnlyMethod(method?: string): boolean {
        if (!method) {
            return true;
        }

        method = method.toUpperCase();
        return READ_ONLY_METHODS.has(method);
    }

    public async refreshCSRFToken(): Promise<string> {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const response = (await this.fetch('/api/v1/system/csrf', {
            method: 'POST',
            data: { refresh: true },
            disableCSRF: true,
        })) as CSRFRequestResponse;

        this.csrfToken = response.csrf_token;
        return response.csrf_token;
    }

    public async getCSRFToken(): Promise<string> {
        return this.csrfToken ?? this.refreshCSRFToken();
    }

    public async fetch(url: string, info?: APIRequestInfo): Promise<unknown> {
        const headers: Record<string, string> = { ...info?.headers };
        const init: RequestInit = { headers };

        if (info) {
            init.method = info.method;

            if (info.data) {
                init.body = JSON.stringify(info.data);
                headers['Content-Type'] = 'application/json';
            } else if (info.body) {
                init.body = info.body;
            }
        }

        if (!info?.disableCSRF && !APIAccessor.isReadOnlyMethod(init.method)) {
            headers['CSRF-Token'] = await this.getCSRFToken();
        }

        const response = await fetch(url, init);

        if (response.status < 200 || response.status > 299) {
            let desc;
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                const data = (await response.json()) as { error: string };
                desc = data.error;
            } catch (error: unknown) {
                logError(error);
            }

            throw new HttpError(response.status, desc ?? response.statusText);
        }

        return response.json();
    }
}
